/**
 * Suggestion renderer that bridges the RichTextEditor's trigger system
 * to the existing MacroTypeahead popover UI.
 */

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { SuggestionCallbackProps, SuggestionRenderer, SuggestionItem } from '@chatsdet/ui';
import type { TypeaheadEntry } from '@chatsdet/types';

interface MacroSuggestionPopoverProps {
  items: SuggestionItem[];
  command: (item: SuggestionItem) => void;
  clientRect: (() => DOMRect | null) | null;
}

const SOURCE_LABELS: Record<string, string> = {
  inputValue: 'Input',
  macro: 'Macro',
  extracted: 'Extracted',
  credential: 'Credential',
  env: 'Environment',
  childFlowOutput: 'Called Flow',
  unknown: 'Variable',
};

const PopoverContent: React.FC<MacroSuggestionPopoverProps & { highlightedIndex: number }> = ({
  items,
  command,
  highlightedIndex,
}) => {
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const highlighted = listRef.current?.children[highlightedIndex] as HTMLElement | undefined;
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  if (items.length === 0) return null;

  const hasMacros = items.some((e) => e.kind === 'macro');
  const hasVariables = items.some((e) => e.kind === 'variable');
  const showHeaders = hasMacros && hasVariables;

  const macroItems = showHeaders ? items.filter((e) => e.kind === 'macro') : [];
  const variableItems = showHeaders ? items.filter((e) => e.kind === 'variable') : [];

  const renderEntry = (item: SuggestionItem, flatIdx: number) => {
    const entry = item as unknown as TypeaheadEntry;
    return (
      <div
        key={item.id}
        role="option"
        aria-selected={flatIdx === highlightedIndex}
        className={`cursor-pointer px-3 py-2 text-sm ${
          flatIdx === highlightedIndex
            ? 'bg-accent text-accent-foreground'
            : 'text-foreground hover:bg-accent/50'
        }`}
        onMouseDown={(e) => {
          e.preventDefault();
          command(item);
        }}
      >
        {entry.kind === 'macro' ? (
          <>
            <div className="flex items-center justify-between">
              <span className="font-mono font-medium text-blue-600 dark:text-blue-400">
                ${entry.name}({entry.args.map((a) => (a.optional ? `${a.name}?` : a.name)).join(', ')})
              </span>
              <span className="ml-2 text-xs text-muted-foreground truncate max-w-[120px]">
                {entry.example}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">{entry.description}</div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="font-mono font-medium text-emerald-600 dark:text-emerald-400">
                {entry.qualifiedName ? `$var.${entry.qualifiedName}` : `$var.${entry.name}`}
              </span>
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                {SOURCE_LABELS[entry.source] ?? 'Variable'}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">{entry.description}</div>
            {entry.designTimeValue && (
              <div className="text-xs text-muted-foreground/60 mt-0.5 truncate" title={entry.designTimeValue}>
                {entry.designTimeValue}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  let flatIdx = -1;

  return (
    <div
      ref={listRef}
      className="z-50 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
      role="listbox"
    >
      {showHeaders ? (
        <>
          <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 border-b border-border/50">
            Macros
          </div>
          {macroItems.map((item) => {
            flatIdx++;
            return renderEntry(item, flatIdx);
          })}
          <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 border-t border-border/50">
            Variables
          </div>
          {variableItems.map((item) => {
            flatIdx++;
            return renderEntry(item, flatIdx);
          })}
        </>
      ) : (
        items.map((item) => {
          flatIdx++;
          return renderEntry(item, flatIdx);
        })
      )}
    </div>
  );
};

/**
 * Creates a SuggestionRenderer that mounts the macro/variable popover
 * positioned via Tiptap's clientRect callback.
 */
export function createMacroSuggestionRenderer(): SuggestionRenderer {
  let container: HTMLDivElement | null = null;
  let root: Root | null = null;
  let highlightedIndex = 0;
  let currentProps: SuggestionCallbackProps | null = null;

  function render() {
    if (!container || !root || !currentProps) return;

    const rect = currentProps.clientRect?.();
    if (rect) {
      const popoverWidth = 320;
      const viewportWidth = window.innerWidth;
      const left = Math.min(rect.left, viewportWidth - popoverWidth - 8);
      container.style.position = 'fixed';
      container.style.left = `${Math.max(8, left)}px`;
      container.style.top = `${rect.bottom + 4}px`;
      container.style.width = `${popoverWidth}px`;
    }

    root.render(
      <PopoverContent
        items={currentProps.items}
        command={currentProps.command}
        clientRect={currentProps.clientRect}
        highlightedIndex={highlightedIndex}
      />
    );
  }

  return {
    onStart(props) {
      container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.zIndex = '50';
      document.body.appendChild(container);
      root = createRoot(container);
      highlightedIndex = 0;
      currentProps = props;
      render();
    },

    onUpdate(props) {
      currentProps = props;
      // Reset highlight when items change
      highlightedIndex = 0;
      render();
    },

    onKeyDown({ event }) {
      if (!currentProps) return false;
      const itemCount = currentProps.items.length;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        highlightedIndex = Math.min(highlightedIndex + 1, itemCount - 1);
        render();
        return true;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        highlightedIndex = Math.max(highlightedIndex - 1, 0);
        render();
        return true;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        if (itemCount > 0 && currentProps.items[highlightedIndex]) {
          event.preventDefault();
          currentProps.command(currentProps.items[highlightedIndex]!);
          return true;
        }
      }

      if (event.key === 'Escape') {
        return true;
      }

      return false;
    },

    onExit() {
      if (root) {
        root.unmount();
        root = null;
      }
      if (container) {
        container.remove();
        container = null;
      }
      currentProps = null;
      highlightedIndex = 0;
    },
  };
}
